import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Campaign } from "@/types/campaign";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CampaignFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId?: string | null;
  initial?: Campaign | null;
  onSaved?: () => void;
}

export function CampaignFormDialog({ open, onOpenChange, tenantId, initial, onSaved }: CampaignFormDialogProps) {
  const isEdit = Boolean(initial?.campaign_id);
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [status, setStatus] = useState<Campaign["status"]>("active");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setUtmSource(initial?.utm_source || "");
      setUtmMedium(initial?.utm_medium || "");
      setStatus(initial?.status || "active");
    }
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (isEdit && initial) {
        const { error } = await supabase
          .from('calie_campaigns')
          .update({ name, utm_source: utmSource || null, utm_medium: utmMedium || null, status })
          .eq('campaign_id', initial.campaign_id);
        if (error) throw error;
        toast({ title: "Campaign updated" });
      } else {
        if (!tenantId) {
          throw new Error("Missing tenant context. Please sign in again.");
        }
        const { error } = await supabase
          .from('calie_campaigns')
          .insert({ name, utm_source: utmSource || null, utm_medium: utmMedium || null, status, tenant_id: tenantId });
        if (error) throw error;
        toast({ title: "Campaign created" });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err: any) {
      toast({ title: isEdit ? "Failed to update" : "Failed to create", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit campaign" : "New campaign"}</DialogTitle>
          <DialogDescription>Set the campaign name, UTM fields and status.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Summer Promo" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="utm_source">UTM Source</Label>
              <Input id="utm_source" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="google" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="utm_medium">UTM Medium</Label>
              <Input id="utm_medium" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="cpc" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v: any) => setStatus(v)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save changes" : "Create campaign")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
